import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FeatureCarousel from './FeatureCarousel';
import FeatureSections from './FeatureSections';
import FAQSection from './FAQSection';
import VideoModal from './VideoModal';
import Footer from './Footer';

const API_BASE = 'https://api.notely.se';

type Platform = 'ios' | 'android' | 'macos' | 'other';

function isUrl(s: string): boolean {
  return s.startsWith('http://') || s.startsWith('https://');
}

function buildFallbackUrl(code: string, platform: Platform): string {
  if (platform === 'android') return `https://play.google.com/redeem?code=${encodeURIComponent(code)}`;
  if (platform === 'macos') return `https://apps.apple.com/app/notely-ai/id6740462619?platform=mac`;
  return isUrl(code) ? code : `/code/${code}`;
}

function detectPlatform(): Platform {
  const ua = navigator.userAgent;
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Macintosh/.test(ua) && !('ontouchend' in document)) return 'macos';
  return 'other';
}

function platformStoreUrl(): string {
  const p = detectPlatform();
  return p === 'android'
    ? 'https://play.google.com/store/apps/details?id=com.cstate.notelyapp'
    : 'https://apps.apple.com/se/app/notely-ai/id6740462619';
}

async function claimAndRedirect(campaignId: string): Promise<{ ok: boolean }> {
  const platform = detectPlatform();
  const storageKey = `notely_campaign_${campaignId}`;
  const stored = localStorage.getItem(storageKey);

  if (stored) {
    try {
      const res = await fetch(
        `${API_BASE}/c/${campaignId}/verify?code=${encodeURIComponent(stored)}&platform=${platform}`
      );
      if (res.ok) {
        const data = await res.json();
        if (data.valid) {
          window.location.replace(data.redirect_url ?? buildFallbackUrl(stored, platform));
          return { ok: true };
        }
      }
      localStorage.removeItem(storageKey);
    } catch {
      window.location.replace(buildFallbackUrl(stored, platform));
      return { ok: true };
    }
  }

  const res = await fetch(`${API_BASE}/c/${campaignId}/claim`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ platform }),
  });
  if (!res.ok) return { ok: false };
  const data = await res.json();
  if (data.code) {
    localStorage.setItem(storageKey, data.code);
    window.location.replace(data.redirect_url ?? buildFallbackUrl(data.code, platform));
    return { ok: true };
  }
  if (data.fallback_url) {
    window.location.replace(data.fallback_url);
    return { ok: true };
  }
  return { ok: false };
}

// ── Floating bottom bar ───────────────────────────────────────────────────────

interface BarProps {
  primaryLabel: string;
  secondaryLabel: string;
  claimingLabel: string;
  onPrimary: () => void;
  onSecondary: () => void;
  isLoading: boolean;
  error: string | null;
}

const FloatingBottomBar: React.FC<BarProps> = ({
  primaryLabel, secondaryLabel, claimingLabel,
  onPrimary, onSecondary, isLoading, error,
}) => (
  <div style={{
    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50,
    padding: '12px 20px', paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
    background: 'var(--v2-nav-bg)', backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)', borderTop: '1px solid var(--v2-line)',
  }}>
    <div style={{ maxWidth: 480, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <button
          onClick={onPrimary}
          style={{
            width: '100%', background: 'var(--v2-primary)', color: '#fff', border: 'none',
            borderRadius: 12, padding: '13px 16px', fontSize: 15, fontWeight: 600,
            cursor: 'pointer', transition: 'opacity 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.opacity = '0.88')}
          onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
        >
          {primaryLabel}
        </button>
        <button
          onClick={onSecondary}
          disabled={isLoading}
          style={{
            width: '100%', background: 'transparent', color: 'var(--v2-ink)',
            border: '1.5px solid var(--v2-line)', borderRadius: 12,
            padding: '13px 16px', fontSize: 15, fontWeight: 500,
            cursor: isLoading ? 'not-allowed' : 'pointer', opacity: isLoading ? 0.65 : 1,
            transition: 'opacity 0.15s',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          {isLoading ? (
            <>
              <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin inline-block" />
              {claimingLabel}
            </>
          ) : secondaryLabel}
        </button>
      {error && (
        <p style={{ color: '#ef4444', fontSize: 13, textAlign: 'center', margin: 0 }}>{error}</p>
      )}
    </div>
  </div>
);

// ── Hero ──────────────────────────────────────────────────────────────────────

const CampaignHero: React.FC<{ pill: string; title: string; subtitle: string }> = ({
  pill, title, subtitle,
}) => (
  <section style={{ padding: '72px 24px 40px', textAlign: 'center' }}>
    <div
      className="animate-slide-up opacity-0"
      style={{ animationDelay: '0.05s', display: 'inline-block', marginBottom: 20 }}
    >
      <span style={{
        display: 'inline-block', padding: '5px 14px', borderRadius: 999,
        border: '1px solid var(--v2-line)', color: 'var(--v2-primary)',
        fontSize: 13, fontWeight: 500, letterSpacing: 0.2,
      }}>
        {pill}
      </span>
    </div>

    <h1
      className="animate-slide-up opacity-0"
      style={{
        animationDelay: '0.15s',
        fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 800, lineHeight: 1.1, marginBottom: 20,
        background: 'linear-gradient(90deg, var(--v2-primary), #a78bfa)',
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
      }}
    >
      {title}
    </h1>

    <p
      className="animate-slide-up opacity-0"
      style={{
        animationDelay: '0.25s',
        fontSize: 18, lineHeight: 1.65, maxWidth: 560, margin: '0 auto',
        color: 'var(--v2-ink)', opacity: 0.7,
      }}
    >
      {subtitle}
    </p>
  </section>
);

// ── Video section ─────────────────────────────────────────────────────────────

const CampaignVideoSection: React.FC<{ playLabel: string }> = ({ playLabel }) => {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="animate-slide-up opacity-0"
      style={{ animationDelay: '0.35s', maxWidth: 720, margin: '0 auto 48px', padding: '0 24px' }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={e => e.key === 'Enter' && setOpen(true)}
        style={{ position: 'relative', borderRadius: 16, overflow: 'hidden', cursor: 'pointer' }}
      >
        {/* #t=0.1 makes the browser seek to 0.1s and use that frame as the thumbnail */}
        <video
          src="/feature-audio_modal.mp4#t=0.1"
          preload="metadata"
          playsInline
          muted
          style={{ width: '100%', maxHeight: 380, objectFit: 'cover', display: 'block' }}
        />
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
          background: 'rgba(0,0,0,0.2)',
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid rgba(255,255,255,0.45)',
          }}>
            <div style={{
              width: 0, height: 0,
              borderTop: '10px solid transparent', borderBottom: '10px solid transparent',
              borderLeft: '18px solid #fff', marginLeft: 4,
            }} />
          </div>
          <span style={{ color: '#fff', fontSize: 14, fontWeight: 500, opacity: 0.9 }}>{playLabel}</span>
        </div>
      </div>
      {open && <VideoModal src="/feature-audio_modal.mp4" onClose={() => setOpen(false)} />}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

type PageStatus = 'loading' | 'landing' | 'redirecting';

const CampaignLanding: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { t } = useTranslation();

  const [status, setStatus] = useState<PageStatus>('loading');
  const [primaryButtonText, setPrimaryButtonText] = useState<string | null>(null);
  const [secondaryButtonText, setSecondaryButtonText] = useState<string | null>(null);
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  useEffect(() => {
    if (!campaignId) { setStatus('landing'); return; }

    const init = async () => {
      try {
        const res = await fetch(`${API_BASE}/c/${campaignId}`);
        if (res.ok) {
          const data = await res.json();
          setPrimaryButtonText(data.primary_button_text ?? null);
          setSecondaryButtonText(data.secondary_button_text ?? null);

          if (data.use_landing_page === false) {
            setStatus('redirecting');
            await claimAndRedirect(campaignId);
            // Redirect failed — fall through to show landing page
          }
        }
      } catch {
        // API unreachable — show landing page
      }
      setStatus('landing');
    };

    init();
  }, [campaignId]);

  const handleUsePromoCode = async () => {
    if (!campaignId || isLoadingCode) return;
    setCodeError(null);
    setIsLoadingCode(true);
    try {
      const result = await claimAndRedirect(campaignId);
      if (!result.ok) {
        setCodeError(t('campaign.bar.error', 'Something went wrong. Please try again.'));
      }
    } catch {
      setCodeError(t('campaign.bar.error', 'Something went wrong. Please try again.'));
    } finally {
      setIsLoadingCode(false);
    }
  };

  const handleDownload = () => {
    window.open(platformStoreUrl(), '_blank', 'noopener,noreferrer');
  };

  if (status !== 'landing') {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--v2-bg)' }}
      >
        <div className="w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const primaryLabel = primaryButtonText || t('campaign.bar.download', 'Download the app');
  const secondaryLabel = secondaryButtonText || t('campaign.bar.promoCode', 'Use the promotion code');

  return (
    <div className="min-h-screen" style={{ background: 'var(--v2-bg)', color: 'var(--v2-ink)' }}>
      <CampaignHero
        pill={t('campaign.hero.pill', 'Exclusive offer · Limited time')}
        title={t('campaign.hero.title', 'Never take notes manually again')}
        subtitle={t('campaign.hero.subtitle', 'The AI voice note app that records, transcribes and summarises — so you can focus on the conversation.')}
      />
      <CampaignVideoSection playLabel={t('campaign.video.playButton', 'Watch demo')} />
      <FeatureCarousel />
      <FeatureSections />
      <FAQSection />
      <div style={{ paddingBottom: 96 }}>
        <Footer />
      </div>
      <FloatingBottomBar
        primaryLabel={primaryLabel}
        secondaryLabel={secondaryLabel}
        claimingLabel={t('campaign.bar.claiming', 'Claiming…')}
        onPrimary={handleDownload}
        onSecondary={handleUsePromoCode}
        isLoading={isLoadingCode}
        error={codeError}
      />
    </div>
  );
};

export default CampaignLanding;
